import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

// Check-in step 1 "Ihr Hotel" (plan Rev. 3 items 27/28), shown after payment instead of the old wizard.
// Four fields go to POST /api/onboarding/finalize with `schemaVersion: 2`; the server normalizes and
// validates them (website must be public, Instagram handle format) and answers 400 with `field`. A repeat
// send is idempotent server-side, so on 500/network errors the form keeps its data and simply resends.

type Field = "name" | "website" | "instagram" | "linkedinProfile";

const FIELDS: Field[] = ["name", "website", "instagram", "linkedinProfile"];

// Loose client check only, so the button can enable; the server decides.
export function looksLikeWebsite(v: string): boolean {
  return /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/.*)?$/i.test(v.trim());
}

export default function CheckIn() {
  const [values, setValues] = useState<Record<Field, string>>({ name: "", website: "", instagram: "", linkedinProfile: "" });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const finalize = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/onboarding/finalize", {
        schemaVersion: 2,
        name: values.name.trim(),
        website: values.website.trim(),
        instagram: values.instagram.trim(),
        linkedinProfile: values.linkedinProfile.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/companies/me"] });
      window.location.href = "/";
    },
    onError: (err: any) => {
      const field = err?.data?.field as Field | undefined;
      if (err?.status === 400 && field && FIELDS.includes(field)) {
        setFieldErrors({ [field]: err.data.error });
        return;
      }
      if (err?.status === 400 && err?.data?.error) {
        setFormError(err.data.error);
        return;
      }
      setFormError("Das hat gerade nicht geklappt. Ihre Angaben sind noch da, bitte senden Sie sie erneut.");
    },
  });

  const set = (f: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((v) => ({ ...v, [f]: e.target.value }));
    setFieldErrors((errs) => ({ ...errs, [f]: undefined }));
    setFormError(null);
  };

  const canSubmit = values.name.trim() !== "" && looksLikeWebsite(values.website) && !finalize.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canSubmit) finalize.mutate();
  };

  const logout = async () => {
    try { await apiRequest("POST", "/api/auth/logout", {}); } catch { /* ignore */ }
    window.location.href = "/";
  };

  const error = (f: Field) =>
    fieldErrors[f] ? (
      <p className="mt-1 text-sm text-red-600" data-testid={`error-${f}`}>{fieldErrors[f]}</p>
    ) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 px-4 py-12">
      <div className="mx-auto w-full max-w-lg">
        <p className="text-xs font-bold uppercase tracking-widest text-green-700">Check-In, Schritt 1 von 6</p>
        <h1 className="mt-2 text-3xl font-bold text-slate-900">Ihr Hotel</h1>
        <p className="mt-2 text-slate-600">Damit startet Ihr Team sofort die Recherche.</p>

        <Card className="mt-6">
          <CardContent className="pt-6">
            <form onSubmit={onSubmit} className="space-y-4" noValidate data-testid="form-checkin-hotel">
              <div>
                <Label htmlFor="checkin-name">Name Ihres Hotels</Label>
                <Input id="checkin-name" className="mt-1.5" value={values.name} onChange={set("name")}
                  placeholder="z. B. Landhotel Zur Linde" maxLength={120} autoComplete="organization"
                  aria-invalid={!!fieldErrors.name} data-testid="input-checkin-name" />
                {error("name")}
              </div>
              <div>
                <Label htmlFor="checkin-website">Website</Label>
                <Input id="checkin-website" className="mt-1.5" value={values.website} onChange={set("website")}
                  placeholder="www.ihr-hotel.de" inputMode="url" autoComplete="url"
                  aria-invalid={!!fieldErrors.website} data-testid="input-checkin-website" />
                {error("website") ?? (
                  <p className="mt-1 text-xs text-slate-500">Wir lesen Ihre Website, um Ihr Haus kennenzulernen.</p>
                )}
              </div>
              <div>
                <Label htmlFor="checkin-instagram">
                  Instagram <span className="font-normal text-slate-500">(falls vorhanden)</span>
                </Label>
                <Input id="checkin-instagram" className="mt-1.5" value={values.instagram} onChange={set("instagram")}
                  placeholder="@ihrhotel" autoCapitalize="none" aria-invalid={!!fieldErrors.instagram}
                  data-testid="input-checkin-instagram" />
                {error("instagram")}
              </div>
              <div>
                <Label htmlFor="checkin-linkedin">
                  LinkedIn <span className="font-normal text-slate-500">(optional)</span>
                </Label>
                <Input id="checkin-linkedin" className="mt-1.5" value={values.linkedinProfile} onChange={set("linkedinProfile")}
                  placeholder="linkedin.com/company/ihr-hotel" inputMode="url" autoCapitalize="none"
                  aria-invalid={!!fieldErrors.linkedinProfile} data-testid="input-checkin-linkedin" />
                {error("linkedinProfile")}
              </div>

              {formError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="text-checkin-error">
                  {formError}
                </div>
              )}

              <Button type="submit" size="lg" className="w-full" disabled={!canSubmit} data-testid="button-checkin-save">
                {finalize.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern und weiter"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-orange-800" data-testid="text-checkin-guarantee">
          <strong>Start-Garantie:</strong> Ist Ihr Social Media A-Team nicht innerhalb von 7 Tagen startklar, ist Ihr erster Monat kostenlos.
        </p>

        <div className="mt-8 text-center">
          <button onClick={logout} className="text-sm text-slate-400 underline hover:text-slate-600">
            Abmelden
          </button>
        </div>
      </div>
    </div>
  );
}
